import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { useGenerateMerchandisingCollectionPublicLink } from '@/hooks/useGenerateMerchandisingCollectionPublicLink';
import { ConfirmRemoveCollectionModal } from '@/pages/MerchandisingCollections/components/ConfirmRemoveCollectionModal';
import { MerchandisingCollection } from '@/services/MerchandisingCollectionsService';

interface MetaPanelProps {
  collection: MerchandisingCollection;
}

export function MetaPanel({ collection }: MetaPanelProps) {
  const [copiedEndpoint, setCopiedEndpoint] = useState(false);
  const [copiedPublicLink, setCopiedPublicLink] = useState(false);
  const [isConfirmRemoveModalOpen, setIsConfirmRemoveModalOpen] =
    useState(false);

  const endpoint = `https://catalog.api.fashionai.dev${collection.apiPath}`;

  const publicLink = collection.opaqueId
    ? `https://products.fashionai.dev/${collection.opaqueId}/${collection.id}`
    : null;

  const { t } = useTranslation('translation', {
    keyPrefix:
      'pages.MerchandisingCollections.pages.Collection.components.CollectionInfo',
  });

  async function copyEndpoint() {
    try {
      await navigator.clipboard.writeText(endpoint);
      setCopiedEndpoint(true);
      setTimeout(() => setCopiedEndpoint(false), 2000);
    } catch {
      //
    }
  }

  async function copyPublicLink() {
    if (!publicLink) return;

    try {
      await navigator.clipboard.writeText(publicLink);
      setCopiedPublicLink(true);
      setTimeout(() => setCopiedPublicLink(false), 2000);
    } catch {
      //
    }
  }

  const { generatePublicLink, isLoading } =
    useGenerateMerchandisingCollectionPublicLink(collection.id);

  async function handleGeneratePublicLink() {
    try {
      await generatePublicLink();

      toast.success(t('generateLinkSuccess'));
    } catch (error) {
      toast.error(t('generateLinkError'));
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>API Endpoint</Label>

        <div className="flex items-center space-x-2">
          <code className="flex-1 rounded-md bg-muted px-3 py-2 font-mono text-sm">
            {endpoint}
          </code>

          <Button
            variant="outline"
            size="sm"
            onClick={copyEndpoint}
            className="shrink-0 bg-transparent"
          >
            {copiedEndpoint
              ? t('copyButtonLabelState2')
              : t('copyButtonLabelState1')}
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          {t('endpointDescription')}
        </p>
      </div>

      <div className="space-y-2">
        <Label>{t('publicLinkTitle')}</Label>

        {!publicLink && (
          <div>
            <Button
              type="button"
              onClick={handleGeneratePublicLink}
              disabled={isLoading}
            >
              {t('publicLinkButtonLabel')}
            </Button>
          </div>
        )}

        {publicLink && (
          <div className="flex items-center space-x-2">
            <code className="flex-1 rounded-md bg-muted px-3 py-2 font-mono text-sm">
              {publicLink}
            </code>

            <Button
              variant="outline"
              size="sm"
              onClick={copyPublicLink}
              className="shrink-0 bg-transparent"
            >
              {copiedPublicLink
                ? t('copyButtonLabelState2')
                : t('copyButtonLabelState1')}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 border-t pt-4 md:grid-cols-2">
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">{t('createdAt')}</p>

          <p className="text-sm">
            {new Date(collection.createdAt).toLocaleDateString('pt-BR')}
          </p>
        </div>

        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">{t('updatedAt')}</p>

          <p className="text-sm">
            {new Date(collection.updatedAt).toLocaleDateString('pt-BR')}
          </p>
        </div>
      </div>

      <div className="pt-4">
        <Button
          type="button"
          variant="destructive"
          size="sm"
          onClick={() => setIsConfirmRemoveModalOpen(true)}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          {t('removeButtonLabel')}
        </Button>
      </div>

      <ConfirmRemoveCollectionModal
        collectionToRemove={isConfirmRemoveModalOpen ? collection : null}
        onRemove={() => setIsConfirmRemoveModalOpen(false)}
      />
    </div>
  );
}
